import { useState } from 'react';
import { useSearch } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { EU_MEMBER_STATES } from '@kmdx/contracts';
import { t, countryName } from '@/i18n';
import { formatMinor } from '@/lib/format';
import { usePartners, useSavePartner } from '@/api/hooks/catalog';
import type { Partner } from '@/api/types';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Checkbox, Field, Input, Select, Textarea } from '@/components/ui/Field';
import { Dialog } from '@/components/ui/Dialog';
import { Table, TBody, TD, TH, THead, TR, TableEmpty } from '@/components/ui/Table';
import {
  CardList,
  CardListEmpty,
  CardListItem,
  DataRow,
  NarrowOnly,
  WideOnly,
} from '@/components/ui/MobileList';
import { ErrorState, LoadingBlock } from '@/components/ui/State';
import { toast } from '@/stores/toast';

const COUNTRY_OPTIONS = [...EU_MEMBER_STATES, 'CH', 'GB', 'RS', 'TR'].map((code) => ({
  value: code,
  label: `${code} — ${countryName(code)}`,
}));

type Draft = Partial<Partner> & { id?: string };

export function PartnersPage() {
  const search = useSearch({ strict: false }) as { q?: string };
  const [term, setTerm] = useState(search.q ?? '');
  const [draft, setDraft] = useState<Draft | null>(null);

  const { data, isLoading, isError, refetch } = usePartners(term.trim() || undefined);
  const save = useSavePartner();

  const submit = () => {
    if (!draft) return;
    save.mutate(draft, {
      onSuccess: () => {
        toast.ok('Контрагентът е записан.');
        setDraft(null);
      },
    });
  };

  return (
    <div className="flex min-h-full flex-col gap-3 p-3 sm:gap-4 sm:p-4">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="font-display text-lg font-bold tracking-tight">{t.partners.title}</h1>
        <Input
          className="order-3 w-full sm:order-none sm:w-64"
          placeholder="Име, ДДС номер, град…"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          type="search"
          inputMode="search"
          enterKeyHint="search"
          autoComplete="off"
          aria-label={t.common.search}
        />
        <Button
          className="ml-auto"
          variant="primary"
          icon={<Plus className="size-4" />}
          onClick={() => setDraft({ country: 'BG', isCustomer: true, paymentTermsDays: 30 })}
        >
          {t.partners.newPartner}
        </Button>
      </div>

      <Card className="overflow-hidden">
        {isLoading ? (
          <LoadingBlock />
        ) : isError ? (
          <ErrorState onRetry={() => void refetch()} />
        ) : (
          <>
            <NarrowOnly>
              {(data?.items ?? []).length === 0 ? (
                <CardListEmpty>{t.common.empty}</CardListEmpty>
              ) : (
                <CardList>
                  {(data?.items ?? []).map((partner) => (
                    <CardListItem key={partner.id}>
                      <div className="font-medium">{partner.name}</div>
                      {partner.legalName ? (
                        <div className="text-[11px] text-ink-faint">{partner.legalName}</div>
                      ) : null}

                      <div className="mt-1.5 flex flex-wrap gap-1">
                        {partner.isCustomer ? (
                          <Badge tone="info">{t.partners.isCustomer}</Badge>
                        ) : null}
                        {partner.isSubcontractor ? (
                          <Badge tone="accent">{t.partners.isSubcontractor}</Badge>
                        ) : null}
                        {partner.isSupplier ? <Badge>{t.partners.isSupplier}</Badge> : null}
                        {partner.forbidsSubcontracting ? (
                          <Badge tone="warn" title="Нарушението вади застраховката">
                            без преотдаване
                          </Badge>
                        ) : null}
                        {partner.sanctionsHit ? <Badge tone="danger">санкции</Badge> : null}
                      </div>

                      <dl className="mt-2">
                        <DataRow label={t.partners.vatNo}>
                          <span className="font-mono text-xs">{partner.vatNo ?? '—'}</span>
                          {partner.vatValid === false ? (
                            <Badge tone="danger" className="ml-1">
                              невалиден по VIES
                            </Badge>
                          ) : null}
                        </DataRow>
                        <DataRow label={t.partners.country}>
                          {countryName(partner.country)}
                          {partner.city ? ` · ${partner.city}` : ''}
                        </DataRow>
                        <DataRow label={t.partners.paymentTerms}>
                          {partner.paymentTermsDays} дни
                        </DataRow>
                        <DataRow label={t.partners.creditLimit}>
                          {partner.creditLimitMinor
                            ? formatMinor(partner.creditLimitMinor, partner.creditLimitCcy ?? 'EUR')
                            : '—'}
                        </DataRow>
                      </dl>

                      <Button
                        className="mt-2 w-full"
                        variant="secondary"
                        onClick={() => setDraft(partner)}
                      >
                        {t.common.edit}
                      </Button>
                    </CardListItem>
                  ))}
                </CardList>
              )}
            </NarrowOnly>

            <WideOnly>
              <Table>
                <THead>
                  <TR>
                    <TH>{t.partners.name}</TH>
                    <TH>{t.partners.vatNo}</TH>
                    <TH>{t.partners.country}</TH>
                    <TH>{t.partners.roles}</TH>
                    <TH numeric>{t.partners.paymentTerms}</TH>
                    <TH numeric>{t.partners.creditLimit}</TH>
                    <TH />
                  </TR>
                </THead>
                <TBody>
                  {(data?.items ?? []).length === 0 ? (
                    <TableEmpty colSpan={7}>{t.common.empty}</TableEmpty>
                  ) : (
                    (data?.items ?? []).map((partner) => (
                      <TR key={partner.id}>
                        <TD>
                          <div className="font-medium">{partner.name}</div>
                          <div className="text-[11px] text-ink-faint">
                            {partner.legalName ?? ''}
                          </div>
                        </TD>
                        <TD className="font-mono text-xs">
                          {partner.vatNo ?? '—'}
                          {partner.vatValid === false ? (
                            <Badge tone="danger" className="ml-1">
                              невалиден по VIES
                            </Badge>
                          ) : null}
                        </TD>
                        <TD className="whitespace-nowrap">
                          {countryName(partner.country)}
                          <div className="text-[11px] text-ink-faint">{partner.city ?? ''}</div>
                        </TD>
                        <TD>
                          <div className="flex flex-wrap gap-1">
                            {partner.isCustomer ? (
                              <Badge tone="info">{t.partners.isCustomer}</Badge>
                            ) : null}
                            {partner.isSubcontractor ? (
                              <Badge tone="accent">{t.partners.isSubcontractor}</Badge>
                            ) : null}
                            {partner.isSupplier ? <Badge>{t.partners.isSupplier}</Badge> : null}
                            {partner.forbidsSubcontracting ? (
                              <Badge tone="warn" title="Нарушението вади застраховката">
                                без преотдаване
                              </Badge>
                            ) : null}
                            {partner.sanctionsHit ? <Badge tone="danger">санкции</Badge> : null}
                          </div>
                        </TD>
                        <TD numeric>{partner.paymentTermsDays}</TD>
                        <TD numeric>
                          {partner.creditLimitMinor
                            ? formatMinor(partner.creditLimitMinor, partner.creditLimitCcy ?? 'EUR')
                            : '—'}
                        </TD>
                        <TD className="text-right">
                          <Button size="sm" variant="ghost" onClick={() => setDraft(partner)}>
                            {t.common.edit}
                          </Button>
                        </TD>
                      </TR>
                    ))
                  )}
                </TBody>
              </Table>
            </WideOnly>
          </>
        )}
      </Card>

      <Dialog
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={draft?.id ? t.common.edit : t.partners.newPartner}
        width="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setDraft(null)}>
              {t.common.cancel}
            </Button>
            <Button variant="primary" onClick={submit} loading={save.isPending}>
              {t.common.save}
            </Button>
          </>
        }
      >
        {draft ? (
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label={t.partners.name} required className="sm:col-span-2">
              <Input
                data-autofocus
                value={draft.name ?? ''}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </Field>
            <Field label="Юридическо име">
              <Input
                value={draft.legalName ?? ''}
                onChange={(e) => setDraft({ ...draft, legalName: e.target.value })}
              />
            </Field>
            <Field label="ЕИК / рег. номер">
              <Input
                value={draft.registrationNo ?? ''}
                onChange={(e) => setDraft({ ...draft, registrationNo: e.target.value })}
              />
            </Field>
            <Field label={t.partners.vatNo} hint="Проверява се по VIES преди фактуриране">
              <Input
                className="font-mono"
                value={draft.vatNo ?? ''}
                onChange={(e) => setDraft({ ...draft, vatNo: e.target.value })}
              />
            </Field>
            <Field label={t.partners.country} required>
              <Select
                value={draft.country ?? 'BG'}
                onChange={(e) => setDraft({ ...draft, country: e.target.value })}
                options={COUNTRY_OPTIONS}
              />
            </Field>
            <Field label={t.partners.city}>
              <Input
                value={draft.city ?? ''}
                onChange={(e) => setDraft({ ...draft, city: e.target.value })}
              />
            </Field>
            <Field label="Адрес">
              <Input
                value={draft.address ?? ''}
                onChange={(e) => setDraft({ ...draft, address: e.target.value })}
              />
            </Field>
            <Field label="Имейл">
              <Input
                type="email"
                value={draft.email ?? ''}
                onChange={(e) => setDraft({ ...draft, email: e.target.value })}
              />
            </Field>
            <Field label="Телефон">
              <Input
                value={draft.phone ?? ''}
                onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
              />
            </Field>
            <Field label={t.partners.paymentTerms}>
              <Input
                type="number"
                min={0}
                className="font-mono tabular"
                value={draft.paymentTermsDays ?? 30}
                onChange={(e) => setDraft({ ...draft, paymentTermsDays: Number(e.target.value) })}
              />
            </Field>
            <Field label={`${t.partners.creditLimit} (евроцентове)`}>
              <Input
                type="number"
                min={0}
                className="font-mono tabular"
                value={draft.creditLimitMinor ?? ''}
                onChange={(e) =>
                  setDraft({ ...draft, creditLimitMinor: Number(e.target.value) || null })
                }
              />
            </Field>

            <div className="flex flex-wrap gap-4 sm:col-span-2">
              <Checkbox
                label={t.partners.isCustomer}
                checked={draft.isCustomer ?? false}
                onChange={(e) => setDraft({ ...draft, isCustomer: e.target.checked })}
              />
              <Checkbox
                label={t.partners.isSubcontractor}
                checked={draft.isSubcontractor ?? false}
                onChange={(e) => setDraft({ ...draft, isSubcontractor: e.target.checked })}
              />
              <Checkbox
                label={t.partners.isSupplier}
                checked={draft.isSupplier ?? false}
                onChange={(e) => setDraft({ ...draft, isSupplier: e.target.checked })}
              />
              <Checkbox
                label={t.partners.forbidsSubcontracting}
                checked={draft.forbidsSubcontracting ?? false}
                onChange={(e) => setDraft({ ...draft, forbidsSubcontracting: e.target.checked })}
              />
            </div>

            <Field label={t.common.notes} className="sm:col-span-2">
              <Textarea
                rows={2}
                value={draft.notes ?? ''}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
              />
            </Field>
          </div>
        ) : null}
      </Dialog>
    </div>
  );
}
